import JSZip from 'jszip';
import type { FileType } from '@/routes/chat/hooks/use-chat';

/**
 * Download a single file
 */
export function downloadFile(file: FileType): void {
  const blob = new Blob([file.fileContents], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filePath.split('/').pop() || 'file.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download multiple files as a ZIP archive
 */
export async function downloadFilesAsZip(files: FileType[], projectName?: string): Promise<void> {
  const zip = new JSZip();
  
  // Add each file to the ZIP
  for (const file of files) {
    zip.file(file.filePath, file.fileContents);
  }
  
  // Generate the ZIP file
  const content = await zip.generateAsync({ type: 'blob' });
  
  // Create download link
  const url = URL.createObjectURL(content);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName || 'project'}-${new Date().toISOString().slice(0, 10)}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download all project files including bootstrap files
 */
export async function downloadAllFiles(
  files: FileType[], 
  bootstrapFiles: FileType[], 
  projectName?: string
): Promise<void> {
  const allFiles = [...bootstrapFiles, ...files];
  await downloadFilesAsZip(allFiles, projectName);
}

/**
 * Download selected files as ZIP
 */
export async function downloadSelectedFiles(
  selectedFiles: FileType[], 
  projectName?: string
): Promise<void> {
  if (selectedFiles.length === 0) {
    throw new Error('No files selected for download');
  }
  
  if (selectedFiles.length === 1) {
    downloadFile(selectedFiles[0]);
  } else {
    await downloadFilesAsZip(selectedFiles, projectName);
  }
}

/**
 * Get file extension from file path
 */
export function getFileExtension(filePath: string): string {
  const parts = filePath.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

/**
 * Get MIME type for file based on extension
 */
export function getMimeType(filePath: string): string {
  const extension = getFileExtension(filePath).toLowerCase();
  
  const mimeTypes: Record<string, string> = {
    'js': 'application/javascript',
    'jsx': 'application/javascript',
    'ts': 'application/typescript',
    'tsx': 'application/typescript',
    'json': 'application/json',
    'html': 'text/html',
    'css': 'text/css',
    'scss': 'text/scss',
    'sass': 'text/sass',
    'md': 'text/markdown',
    'txt': 'text/plain',
    'xml': 'application/xml',
    'svg': 'image/svg+xml',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'pdf': 'application/pdf',
    'zip': 'application/zip'
  };
  
  return mimeTypes[extension] || 'text/plain';
}
